import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IDataSourceItem, isRecord, JsonObject, JsonPrimitive } from '@se/cp.common';
import { GridUtilitiesService, IBasePatientHistory } from '@se/platform';
import { NotificationService, DataBinding, Logger } from '@se/se.ui.html5';
import { GridOptions } from 'ag-grid-community';
import { isArray, isEmpty, isObject, isUndefined, join, noop, toString } from 'lodash-es';
import { PropertyType, IImportDataBinding, isEntity, ICpEntity, CpEntityList, IDataValidation, DataPresenterType, CpEntity } from '../../cp-configuration/view-model';
import { nameof } from '../../cp-configuration/utils/utils';
import { CpModelService } from '../../cp-model.service';
import { CpImportGridTooltipComponent } from "./import-grid-tooltip/import-grid-tooltip.component";

export type ICpPatientHistory = IBasePatientHistory & Partial<Record<string, PropertyType>>;

@Injectable({
    providedIn: 'root'
})
export class CpImportService {

    constructor(
        private gridUtils: GridUtilitiesService,
        private logger: Logger,
        private modelService: CpModelService,
        private notificationService: NotificationService,
        private translate: TranslateService) {
    }

    getBaseGridOptions(): GridOptions {
        return {
            suppressRowDeselection: true,
            suppressCellFocus: true,
            suppressLoadingOverlay: true,
            localeText: { noRowsToShow: this.translate.instant('NoRowsToShow') },
            columnDefs: [
                {
                    headerName: this.translate.instant('ReportDate'),
                    field: 'reportDate',
                    width: 80,
                    minWidth: 80,
                    valueFormatter: this.gridUtils.dateTimeFormatter('reportDate'),
                    comparator: this.gridUtils.dateComparator,
                    sort: 'desc',
                },
                {
                    headerName: this.translate.instant('Exam'),
                    field: 'examDescription',
                    sortable: false,
                    width: 120,
                    minWidth: 120,
                },
                {
                    headerName: this.translate.instant('BusinessUnit'),
                    field: 'businessUnitDescription',
                    width: 80,
                    minWidth: 80,
                },
            ],
            defaultColDef: { editable: false, sortable: true, resizable: true },
            enableBrowserTooltips: false,
            context: { importCallback: noop },
            components: { tooltipComp: CpImportGridTooltipComponent }
        };
    }

    public extractImportedVaue(dataBinding: DataBinding, data?: Partial<Record<string, PropertyType>>): PropertyType {

        let importedValue: PropertyType;

        if (!data) {
            const entitySource = this.modelService.getEntity(dataBinding.entity);
            importedValue = entitySource?.getPropertyValue(dataBinding.propertyName) ?? null;
        }
        else {
            importedValue = data[dataBinding.toStringSimple()] ?? null;
        }

        return importedValue;
    }

    public updateBindingTarget(dataBindings: IImportDataBinding[], data?: Partial<Record<string, PropertyType>>) {

        dataBindings.forEach(binding => {

            if (!binding.dataBindingTarget) {
                throw new Error("Import control: target binding not found for property " + binding.dataBindingSource.toStringSimple());
            }

            const importedValue: PropertyType = this.extractImportedVaue(binding.dataBindingSource, data);

            const dataBindingTarget = binding.dataBindingTarget;

            if (isEntity(importedValue)) {
                this.updateComplexTarget(importedValue, dataBindingTarget);
            }
            else {
                //valore singolo
                this.modelService.setValue(dataBindingTarget, importedValue);
            }
        });

        if (dataBindings.length > 0) {
            const gridHeaders = join(dataBindings.map(b => b.gridHeader), ", ");
            const importedDataMessage = this.translate.instant('ImportedData') + gridHeaders;
            this.notificationService.success(importedDataMessage, this.translate.instant('ImportSucceedeed'));
        }
    }

    /**
     * Returns true if all data bindings are null or empty or
     * if at least one data binding is on error
     * @param data
     */
    public validateData(data: ICpPatientHistory, dataBindings: IImportDataBinding[]): IDataValidation {

        //mi basta una cella non ancora caricata o in errore per disabilitare l'import su quella riga
        let isAllColumnsEmpty = true;

        for (let dataBinding of dataBindings) {

            const dataBindingSource = dataBinding.dataBindingSource;
            let cellContent = this.dataPresenter(data, dataBindingSource);
            let dataValidation: IDataValidation;

            if (!isObject(cellContent)) {
                cellContent = cellContent?.trim()?.toLowerCase() ?? '';

                dataValidation = this.validateCellContentString(cellContent);
            }
            else {

                dataValidation = this.validateCellContentObject(cellContent);
            }

            if (dataValidation.onError)
                return dataValidation;

            isAllColumnsEmpty = isAllColumnsEmpty && dataValidation.isEmpty;
        }

        return { isEmpty: isAllColumnsEmpty, onError: false };
    }

    private validateCellContentString(cellContent: string): IDataValidation {
        if (cellContent.startsWith(this.translate.instant('ImportNoData').toLowerCase()) ||
            cellContent.startsWith('loading...')) {
            return { isEmpty: false, onError: true };
        }

        const isEmpty = (cellContent === '');
        return {
            isEmpty: isEmpty,
            onError: false
        };
    }

    private validateCellContentObject(cellContent: object): IDataValidation {

        return {
            isEmpty: isEmpty(cellContent),
            onError: false
        };
    }

    /**
     * A partire dal dato, ritorna una descrizione o un oggetto da visualizzare alla griglia
     * @param data
     * @param dataBinding
     */
    public dataPresenter(data: ICpPatientHistory, dataBinding: DataBinding, renderedProperty?: string): DataPresenterType {

        if (!isRecord(data))
            return null;

        let bindedField: unknown = data[dataBinding.toStringSimple()];

        //nel caso che il campo sia un oggetto complesso, di default considero che sia una combo
        //perchè viene passato un guid e viene renderizzata una description (o il path nel caso di albero)
        //nel caso di import di una griglia, la proprietà renderizzata è quella effettivamente importata
        let renderedPropertyIfFieldIsObject = renderedProperty;
        let renderedSecondPropertyIfFieldIsObject: string;

        if (isEmpty(renderedPropertyIfFieldIsObject)) {
            renderedPropertyIfFieldIsObject = nameof<IDataSourceItem>('Path');
            //questa riga sotto è solo per sicurezza, nel caso Path sia vuoto cerco nella Description
            renderedSecondPropertyIfFieldIsObject = nameof<IDataSourceItem>('Description');
        }

        if (isEntity(bindedField)) {
            bindedField = bindedField.toJSON();
        }

        if (isArray(bindedField)) {
            return bindedField.reduce<DataPresenterType>((prevData, field: JsonPrimitive | ICpEntity) => {
                const fieldData: DataPresenterType = this.dataPresenterField(field, <string>renderedPropertyIfFieldIsObject, renderedSecondPropertyIfFieldIsObject);

                if (!prevData || (prevData === ''))
                    return fieldData;

                if (isObject(prevData))
                    return prevData;

                if (isObject(fieldData))
                    return fieldData;

                return prevData + ", " + fieldData;
            }, '');
        }

        // TODO: Binding avanzato.
        return this.dataPresenterField(bindedField, dataBinding.propertyName);
    }

    // #region Privates

    /**
     * Importa una intera lista o un oggetto
     */
    private updateComplexTarget(importedValue: ICpEntity, dataBindingTarget: DataBinding) {
        let entitiesSource = importedValue.getAllEntities(new DataBinding());

        if (!!entitiesSource) {

            const root = this.modelService.getEntity(new DataBinding());
            let entityTarget = root?.navigateToPath(dataBindingTarget, true);

            if (entityTarget instanceof CpEntityList) {

                entityTarget = this.modelService.makeEntity<CpEntityList>([] as JsonObject[], dataBindingTarget, entityTarget.parent);
                //const newEntityTarget : ICpEntity = this.modelService.makeEntity([] as Json[], dataBindingTarget.toStringSimple());
                //if (entityTarget.activeEntities.length > 0) {
                //    //svuoto la collection e ne aggiungo una nuova
                //    this.modelService.notifyChange(dataBindingTarget.full, dataBindingTarget.toStringSimple(), []);
                //    entityTarget = <ICpEntity>this.modelService.getEntity(dataBindingTarget.full);
                //}


                for (let i = 0; i < entitiesSource.length; i++) {

                    //creo un elemento dentro entityTarget per ogni entity in entitiesSource
                    let currentTarget = this.copyEntity(entitiesSource[i], dataBindingTarget.toStringSimple(), `${dataBindingTarget.toStringSimple()}.${i}`, entityTarget);
                    entityTarget.addEntityToList(new DataBinding(), currentTarget);
                }

                //notifico l'aggiornamento della collection con gli elementi in più
                this.modelService.setValue(dataBindingTarget, entityTarget);
            }
            else {

                //creo e notifico il nuovo oggetto
                const entityParent = this.modelService.getEntity(dataBindingTarget.entity);
                if (!entityParent) {
                    this.logger.error("Impossible to import data: dataBinding Target is wrong: " + dataBindingTarget.toString());
                    throw new Error("Impossible to import data: dataBinding Target is wrong");
                }

                entityTarget = this.copyEntity(entitiesSource[0], dataBindingTarget.toStringSimple(), `${dataBindingTarget.toStringSimple()}`, entityParent);

                //notifico l'aggiornamento dell'oggetto
                this.modelService.setValue(dataBindingTarget, entityTarget);
            }

        }
    }

    private copyEntity(currentSource: ICpEntity, originalPath: string, path: string, entityParent: ICpEntity): ICpEntity {

        //creo un elemento dentro entityTarget per ogni entity in entitiesSource
        // TODO: Binding avanzato
        let currentTarget = this.modelService.makeEntityFromPlaceholder(new DataBinding(path), entityParent) as ICpEntity;

        if (!currentTarget) {
            currentTarget = this.modelService.makeEntity<CpEntity>(currentSource.toJSON() as JsonObject) as ICpEntity;
        }

        //aggiorno le proprietà corrispondenti
        this.copyEntityProperties(currentSource, currentTarget);

        return currentTarget;
    }

    /**
     * Copia (con l'eclusione del Guid) le proprietà da entityFrom a entityTo
     */
    private copyEntityProperties(entityFrom: ICpEntity, entityTo: ICpEntity) {

        if (!entityFrom)
            return;

        //aggiorno le proprietà corrispondenti
        entityFrom.forOwn((prop, value) => {
            if (prop == 'Id' || prop == 'guid') {
                return;
            }

            if (entityTo.hasProperty(prop)) {
                entityTo.setPropertyValue(prop, value);
            }
        });
    }

    private dataPresenterField(field: unknown, propertyName: string, secondPropertyName?: string): DataPresenterType {

        if (isRecord(field)) {

            let fieldValue = field[propertyName];

            if (isUndefined(fieldValue) && !isUndefined(secondPropertyName)) {
                //provo a cercare in secondPropertyName, se anche da quella non ottengo nulla ritorno il campo stesso
                fieldValue = field[secondPropertyName]
            }

            if (isUndefined(fieldValue))
                return field;

            return this.extractText(fieldValue);
        }

        return this.extractText(field);
    }

    private extractText(field: unknown): string | null {

        const str = toString(field);

        const a = document.createElement('div');
        a.innerHTML = str;

        //il testo potrebbe essere un html. Lo converto in testo normale per la visualizzazione.
        for (let c = a.childNodes, i = c.length; i--;) {
            if (c[i].nodeType == 1) {
                return a.textContent;
            }
        }

        return str;
    }

    // #endregion
}
